import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/toaster";

import Landing from "@/pages/Landing";
import Maintenance from "@/pages/Maintenance";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Me from "@/pages/Me";
import PasswordReset from "@/pages/PasswordReset";
import SuperAdmin from "@/pages/admin/SuperAdmin";
import TenantApp from "@/pages/tenant/TenantApp";
import DriverView from "@/pages/driver/DriverView";
import PortalHome from "@/pages/portal/PortalHome";
import PortalLogin from "@/pages/portal/PortalLogin";
import CustomerAccount from "@/pages/portal/CustomerAccount";
import BookingFlow from "@/pages/portal/BookingFlow";
import PortalReferences from "@/pages/portal/PortalReferences";
import PortalInterestMap from "@/pages/portal/PortalInterestMap";
import PortalLegalPage from "@/pages/portal/PortalLegalPage";
import TonnevaLegalPage from "@/pages/TonnevaLegalPage";
import ConsentRevokePage from "@/pages/ConsentRevokePage";
import GdprRequestPage from "@/pages/GdprRequestPage";
import { CheckoutSuccess, CheckoutCancel } from "@/pages/portal/CheckoutPages";
import api from "@/lib/api";
import { PortalDomainContext } from "@/contexts/PortalDomainContext";

// Bekannte Plattform-Hosts — alles andere wird als Mandanten-Custom-Domain behandelt.
const KNOWN_HOST_RE = /(?:^|\.)tonneva\.de$|\.preview\.emergentagent\.com$|\.emergent\.host$|(?:^|\.)emergentagent\.com$|^localhost$|^127\.0\.0\.1$/i;

function useCustomDomain() {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isCustomDomain = !!host && !KNOWN_HOST_RE.test(host);
  const [state, setState] = useState({ slug: null, resolving: isCustomDomain });
  useEffect(() => {
    if (!isCustomDomain) return undefined;
    let active = true;
    const bare = host.replace(/^www\./, "");
    api.get(`/tenants/by-domain/${bare}`)
      .then((r) => { if (active) setState({ slug: r.data?.slug || null, resolving: false }); })
      .catch(() => { if (active) setState({ slug: null, resolving: false }); });
    return () => { active = false; };
  }, [isCustomDomain, host]);
  return { ...state, isCustomDomain };
}

function PortalDomainRoutes({ slug }) {
  return (
    <PortalDomainContext.Provider value={{ slug, customDomain: true }}>
      <Routes>
        <Route path="/" element={<PortalHome />} />
        <Route path="/login" element={<PortalLogin />} />
        <Route path="/account" element={<CustomerAccount />} />
        <Route path="/book" element={<BookingFlow />} />
        <Route path="/referenzen" element={<PortalReferences />} />
        <Route path="/karte" element={<PortalInterestMap />} />
        <Route path="/impressum" element={<PortalLegalPage page="impressum" />} />
        <Route path="/agb" element={<PortalLegalPage page="agb" />} />
        <Route path="/datenschutz" element={<PortalLegalPage page="datenschutz" />} />
        <Route path="/legal/widerruf" element={<ConsentRevokePage />} />
        <Route path="/legal/auskunftsersuchen" element={<GdprRequestPage />} />
        <Route path="/checkout/success" element={<CheckoutSuccess />} />
        <Route path="/checkout/cancel" element={<CheckoutCancel />} />
        <Route path="*" element={<PortalHome />} />
      </Routes>
    </PortalDomainContext.Provider>
  );
}

function AppRouter() {
  const location = useLocation();
  const domain = useCustomDomain();
  // Handle OAuth hash redirect synchronously to prevent race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Mandanten-Custom-Domain (z. B. tonne-sauber.de): Portal am Root rendern, Maintenance-Gate umgehen
  if (domain.isCustomDomain) {
    if (domain.resolving) {
      return (
        <div
          style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", fontFamily: "Inter, system-ui, sans-serif" }}
          data-testid="custom-domain-loading"
        >
          Lädt…
        </div>
      );
    }
    if (domain.slug) {
      return <PortalDomainRoutes slug={domain.slug} />;
    }
    // Domain nicht zugeordnet → normale Seite als Fallback
  }
  // Maintenance-Gate: on `/`, show Coming-Soon unless preview key granted.
  // Bypass via `?preview=<KEY>` (persisted to localStorage as `tonneva_preview`).
  const previewKey = process.env.REACT_APP_PREVIEW_KEY || "gtr-2026";
  if (location.pathname === "/" && !location.hash?.includes("session_id=")) {
    const params = new URLSearchParams(location.search);
    const q = params.get("preview");
    if (q && q === previewKey) {
      try { localStorage.setItem("tonneva_preview", previewKey); } catch (e) { console.warn("[App] localStorage nicht verfügbar", e); }
    }
    const granted =
      (typeof localStorage !== "undefined" && localStorage.getItem("tonneva_preview") === previewKey) ||
      (q && q === previewKey);
    if (!granted) return <Maintenance />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/reset" element={<PasswordReset />} />
      <Route path="/me" element={<Me />} />
      <Route path="/admin" element={<SuperAdmin />} />
      <Route path="/app" element={<TenantApp />} />
      <Route path="/driver" element={<DriverView />} />
      <Route path="/portal/:slug" element={<PortalHome />} />
      <Route path="/portal/:slug/login" element={<PortalLogin />} />
      <Route path="/portal/:slug/account" element={<CustomerAccount />} />
      <Route path="/portal/:slug/book" element={<BookingFlow />} />
      <Route path="/portal/:slug/referenzen" element={<PortalReferences />} />
      <Route path="/portal/:slug/karte" element={<PortalInterestMap />} />
      <Route path="/portal/:slug/impressum" element={<PortalLegalPage page="impressum" />} />
      <Route path="/portal/:slug/agb" element={<PortalLegalPage page="agb" />} />
      <Route path="/portal/:slug/datenschutz" element={<PortalLegalPage page="datenschutz" />} />
      <Route path="/impressum" element={<TonnevaLegalPage />} />
      <Route path="/agb" element={<TonnevaLegalPage />} />
      <Route path="/datenschutz" element={<TonnevaLegalPage />} />
      <Route path="/legal/widerruf" element={<ConsentRevokePage />} />
      <Route path="/legal/auskunftsersuchen" element={<GdprRequestPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/checkout/cancel" element={<CheckoutCancel />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  );
}
